import React, { useState, useEffect } from 'react';
import { ScrollText, Check, CreditCard, AlertCircle, Clock, Crown } from 'lucide-react';
import { stripeService } from '../../services/stripeService';
import { useAuth } from '../../contexts/AuthContext';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useStripeContext } from '../../contexts/StripeContext';
import { supabase } from '../../lib/supabase';
import SidebarNavigation from '../../components/navigation/SidebarNavigation';
import SubscriptionCancellationModal from '../subscription-management-screen/SubscriptionCancellationModal';

// Payment form component
const PaymentForm = ({ selectedPlan, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe?.createPaymentMethod({
        type: 'card',
        card: elements?.getElement(CardElement),
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      // Call service to create subscription
      await stripeService?.createSubscription(
        selectedPlan?.psychologistId,
        selectedPlan?.plan,
        paymentMethod?.id
      );

      onSuccess();
    } catch (err) {
      setError(err?.message || 'Erro ao processar pagamento');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Informações do cartão
        </label>
        <div className="border border-gray-300 rounded-lg p-4 bg-white">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#1f2937',
                  '::placeholder': {
                    color: '#9ca3af',
                  },
                },
                invalid: {
                  color: '#ef4444',
                },
              },
            }}
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Processando...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Confirmar Pagamento
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Plan card component
const PlanCard = ({ plan, isCurrentPlan, onSelect, loading }) => {
  const planDetails = stripeService?.getPlanDetails(plan);
  
  return (
    <div className={`border rounded-xl p-6 ${isCurrentPlan ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}>
      {isCurrentPlan && (
        <div className="flex items-center gap-2 text-blue-600 mb-4">
          <Crown className="w-5 h-5" />
          <span className="text-sm font-medium">Plano Atual</span>
        </div>
      )}
      <h3 className="text-xl font-bold text-gray-900 mb-2">{planDetails?.name}</h3>
      <div className="mb-6">
        <span className="text-3xl font-bold text-gray-900">
          {stripeService?.formatAmount(planDetails?.price)}
        </span>
        <span className="text-gray-600">/mês</span>
      </div>
      <ul className="space-y-3 mb-6">
        {planDetails?.features?.map((feature, index) => (
          <li key={index} className="flex items-start gap-2">
            <Check className={`${feature.length < 1 ? 'opacity-0 ' : 'w-5 h-5 text-green-600 flex-shrink-0 mt-0.5'}`} />
            <span className="text-sm text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={() => onSelect(plan)}
        disabled={isCurrentPlan || loading}
        className={`w-full py-2 rounded-lg font-medium ${
          isCurrentPlan 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        {isCurrentPlan ? 'Plano Ativo' : 'Selecionar Plano'}
      </button>
    </div>
  );
};

export default function SubscriptionManagementScreen() {
  const { user } = useAuth();
  const { stripePromise, stripeOptions } = useStripeContext();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [psychologistId, setPsychologistId] = useState(null);
  const [showCancellationModal, setShowCancellationModal] = useState(false);

  useEffect(() => {
    loadSubscriptionData();
  }, [user]);

  const loadSubscriptionData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Get psychologist profile
      const { data: psychologist } = await supabase?.from('psychologists')?.select('id')?.eq('user_id', user?.id)?.single();
      
      if (psychologist) {
        setPsychologistId(psychologist?.id);

        // Load subscription
        const subscriptionData = await stripeService?.getSubscription(psychologist?.id);

        setSubscription(subscriptionData);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan({
      plan,
      psychologistId
    });
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    setSelectedPlan(null);
    loadSubscriptionData();
  };

  const handleCancelSubscription = () => {
    setShowCancellationModal(true);
  };

  const handleConfirmCancellation = async (cancellationData) => {
    try {
      // Call cancel subscription with reason and feedback
      await stripeService?.cancelSubscription(subscription?.stripe_subscription_id);
      
      // Optionally save cancellation feedback
      if (cancellationData?.reason || cancellationData?.feedback) {
        await supabase?.from('subscription_history')?.insert({
          subscription_id: subscription?.id,
          event_type: 'cancellation_feedback',
          metadata: {
            reason: cancellationData?.reason,
            feedback: cancellationData?.feedback
          }
        });
      }
      
      loadSubscriptionData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  };

  const handleDowngradeFromCancellation = async (targetPlan) => {
    try {
      await stripeService?.updateSubscriptionPlan(
        subscription?.stripe_subscription_id,
        targetPlan
      );
      loadSubscriptionData();
    } catch (error) {
      console.error('Error downgrading subscription:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const daysRemaining = subscription ? stripeService?.getDaysRemaining(subscription) : 0;
  const isTrialing = subscription?.status === 'trialing';

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#d3d3d3' }}>
      <SidebarNavigation />
      <main className="flex-1 lg:ml-64">
        <div className="p-3 sm:p-4 lg:p-4 pt-4 sm:pt-4 lg:pt-6 lg:mt-0 max-w-full overflow-x-hidden">
          {/* Header - Standardized Format */}
          <div className="mb-8">
            <div className="bg-gray-100 rounded-xl shadow-lg shadow-md p-6 max-w-6xl">

              {/* Desktop */}
              <div className="hidden md:flex items-center gap-3 mb-2 flex-nowrap">
                <ScrollText className="w-7 h-7 text-[#969696] flex-shrink-0" />

                <h1 className="text-3xl font-bold text-[#969696] font-manrope whitespace-nowrap">
                  Gerenciar Assinatura
                </h1>
              </div>

              {/* Mobile */}
              <div className="md:hidden flex items-center gap-2 mb-1">
                <ScrollText className="w-7 h-7 text-[#969696] flex-shrink-0" />

                <h1 className="text-2xl font-bold text-[#969696] font-manrope">
                  Gerenciar Assinatura
                </h1>
              </div>

              {/* Desktop - Mostra subtítulo */}
              <p className="hidden md:block text-[#969696] font-manrope text-lg">
                Escolha seu plano ideal
              </p>
              
            </div>
          </div>            

        {/* Current Status */}
        {subscription && (
          <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Assinatura
                  </h3>
                  <p className="text-sm text-gray-600">
                    {isTrialing ? (
                      <>Período de teste: <strong>{daysRemaining} dias restantes</strong></>
                    ) : (
                      <>Situação: <strong>{daysRemaining > 0 ? 'Ativa, restando ' + daysRemaining + ' dias até renovação' : ' Expirada'}</strong></>
                    )}
                  </p>
                  <p className="text-sm text-gray-600">
                    Plano: {daysRemaining > 0 ? stripeService?.getPlanDetails(subscription?.plan)?.name : 'Ausente'}
                  </p>
                </div>
              </div>
              
              {subscription?.status === 'active' && (
                <button
                  onClick={handleCancelSubscription}
                  className="px-4 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                >
                  Cancelar Assinatura
                </button>
              )}
            </div>
          </div>
        )}

        {/* Payment Form Modal */}
        {showPaymentForm && selectedPlan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Confirmar Pagamento
              </h2>
              <p className="text-gray-600 mb-6">
                Plano: {stripeService?.getPlanDetails(selectedPlan?.plan)?.name} - {' '}
                {stripeService?.formatAmount(stripeService?.getPlanDetails(selectedPlan?.plan)?.price)}/mês
              </p>
              
              <Elements stripe={stripePromise} options={stripeOptions}>
                <PaymentForm
                  selectedPlan={selectedPlan}
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => {
                    setShowPaymentForm(false);
                    setSelectedPlan(null);
                  }}
                />
              </Elements>
            </div>
          </div>
        )}

        {/* Comprehensive Cancellation Modal */}
        {showCancellationModal && subscription && (
          <SubscriptionCancellationModal
            subscription={subscription}
            onClose={() => setShowCancellationModal(false)}
            onConfirmCancel={handleConfirmCancellation}
            onDowngrade={handleDowngradeFromCancellation}
          />
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-8">
          <PlanCard
            plan="basic"
            isCurrentPlan={subscription?.plan === 'basic' && daysRemaining > 0}
            onSelect={handlePlanSelect}
            loading={loading}
          />
          <PlanCard
            plan="advanced"
            isCurrentPlan={subscription?.plan === 'advanced' && daysRemaining > 0}
            onSelect={handlePlanSelect}
            loading={loading}
          />
          <PlanCard
            plan="pro"
            isCurrentPlan={subscription?.plan === 'pro' && daysRemaining > 0}
            onSelect={handlePlanSelect}
            loading={loading}
          />
        </div>

        {/* Trial Info */}
        {!subscription && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">
                  Período de Teste Gratuito
                </h3>
                <p className="text-sm text-blue-800">
                  Experimente todos os recursos gratuitamente por 15 dias. 
                  Sem necessidade de cartão de crédito para começar.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
    </div>

  );
}